import React, { useState, Suspense, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { getAllEmailLogsAPI, getLogStatsAPI } from "../../API/EmailLogAPI";
import { getAllEmailTemplatesAPI } from "../../API/EmailTemplateAPI";
import ButtonLoader from "../../components/button/ButtonLoader";
import toast from "react-hot-toast";
import {
  FiMail,
  FiCheckCircle,
  FiXCircle,
  FiFilter,
  FiCalendar,
  FiUser,
  FiFileText,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";

const Wrapper = React.lazy(() => import("../../components/wrapper/Wrapper"));

const EmailLogs = () => {
  const [searchParams] = useSearchParams();
  const templateIdFromUrl = searchParams.get("templateId") || "";
  const scheduleIdFromUrl = searchParams.get("scheduleId") || "";

  const [loader, setLoader] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ sent: 0, failed: 0, total: 0 });
  const [templates, setTemplates] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });

  const [filters, setFilters] = useState({
    templateId: templateIdFromUrl,
    scheduleId: scheduleIdFromUrl,
    status: "",
    recipientEmail: "",
    startDate: "",
    endDate: "",
  });
  const [showFilters, setShowFilters] = useState(!!(templateIdFromUrl || scheduleIdFromUrl));

  // Sync URL params to filters when navigating with ?templateId= or ?scheduleId=
  useEffect(() => {
    if (templateIdFromUrl || scheduleIdFromUrl) {
      setFilters((prev) => ({
        ...prev,
        templateId: templateIdFromUrl,
        scheduleId: scheduleIdFromUrl,
      }));
      setShowFilters(true);
    }
  }, [templateIdFromUrl, scheduleIdFromUrl]);

  const fetchLogs = React.useCallback(async (page = 1) => {
    setLoader(true);
    try {
      const params = {
        page,
        limit: 20,
        ...(filters.templateId && { templateId: filters.templateId }),
        ...(filters.scheduleId && { scheduleId: filters.scheduleId }),
        ...(filters.status && { status: filters.status }),
        ...(filters.recipientEmail && { recipientEmail: filters.recipientEmail }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
      };
      const res = await getAllEmailLogsAPI(params);
      setLogs(res.data.logs || []);
      setPagination(res.data.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch (error) {
      toast.error("Failed to load email logs");
      setLogs([]);
    } finally {
      setLoader(false);
    }
  }, [filters]);

  const fetchStats = React.useCallback(async () => {
    try {
      const params = {};
      if (filters.templateId) params.templateId = filters.templateId;
      if (filters.scheduleId) params.scheduleId = filters.scheduleId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const res = await getLogStatsAPI(params);
      setStats(res.data.stats || { sent: 0, failed: 0, total: 0 });
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  }, [filters]);

  const fetchTemplates = async () => {
    try {
      const res = await getAllEmailTemplatesAPI();
      setTemplates(res.data.templates || []);
    } catch (error) {
      console.error("Failed to load templates:", error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    fetchLogs(1);
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    fetchLogs(1);
    fetchStats();
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({
      templateId: "",
      scheduleId: "",
      status: "",
      recipientEmail: "",
      startDate: "",
      endDate: "",
    });
    fetchLogs(1);
    fetchStats();
    setShowFilters(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchLogs(newPage);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleString();
  };

  return (
    <Suspense
      fallback={
        <div className="w-full pt-40 flex justify-center items-center">
          <ButtonLoader />
        </div>
      }
    >
      <Wrapper>
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <FiMail className="w-8 h-8 text-[--primary]" />
              Email Logs
            </h1>
            <p className="text-gray-600 mt-2">
              View sent and failed email records from scheduled campaigns
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setShowFilters(!showFilters);
                if (!showFilters) fetchStats();
              }}
              className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center gap-2"
            >
              <FiFilter className="w-4 h-4" />
              Filters
            </button>
            <button
              onClick={() => {
                handleClearFilters();
                fetchLogs(1);
                fetchStats();
                toast.success("Refreshed");
              }}
              className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center gap-2"
            >
              <FiRefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="mb-6 p-6 bg-gray-50 border border-gray-200 rounded-xl space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FiFilter className="w-4 h-4" />
              Filter logs
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>
                <select
                  value={filters.templateId}
                  onChange={(e) => handleFilterChange("templateId", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent"
                >
                  <option value="">All templates</option>
                  {templates.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.templateType} – {t.subject?.slice(0, 40)}
                      {t.subject?.length > 40 ? "…" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent"
                >
                  <option value="">All</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient email
                </label>
                <input
                  type="text"
                  value={filters.recipientEmail}
                  onChange={(e) =>
                    handleFilterChange("recipientEmail", e.target.value)
                  }
                  placeholder="Search by email…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-1" />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange("startDate", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange("endDate", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 font-medium"
              >
                Apply
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <FiFileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <FiCheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Sent</p>
              <p className="text-2xl font-bold text-green-700">{stats.sent}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <FiXCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Failed</p>
              <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
            </div>
          </div>
        </div>

        {/* Logs table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loader ? (
            <div className="py-20 flex justify-center">
              <ButtonLoader />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-20 text-center text-gray-500">
              <FiMail className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="font-medium">No email logs found</p>
              <p className="text-sm mt-1">
                Logs appear here when scheduled campaigns send emails.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                        Recipient
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                        Subject
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                        Template / Schedule
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                        Sent at
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                        Error
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log._id} className="hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${log.status === "sent"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                              }`}
                          >
                            {log.status === "sent" ? (
                              <FiCheckCircle className="w-3.5 h-3.5" />
                            ) : (
                              <FiXCircle className="w-3.5 h-3.5" />
                            )}
                            {log.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <FiUser className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {log.recipientEmail}
                              </p>
                              {log.recipientName && (
                                <p className="text-xs text-gray-500">
                                  {log.recipientName}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700 max-w-[200px] truncate">
                          {log.subject}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <div>
                            {log.emailTemplate && (
                              <p className="font-medium text-gray-900">
                                {log.emailTemplate.templateType}
                              </p>
                            )}
                            {log.emailSchedule && (
                              <p className="text-xs text-gray-500">
                                {log.emailSchedule.name}
                              </p>
                            )}
                            {!log.emailTemplate && !log.emailSchedule && "—"}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                          <span className="flex items-center gap-1">
                            <FiCalendar className="w-4 h-4 text-gray-400" />
                            {formatDate(log.sentAt)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-red-600 max-w-[180px]">
                          {log.status === "failed" && log.errorMessage ? (
                            <span
                              className="truncate block"
                              title={log.errorMessage}
                            >
                              {log.errorMessage}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing page {pagination.page} of {pagination.totalPages} (
                    {pagination.total} total)
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-sm font-medium">
                      {pagination.page} / {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Wrapper>
    </Suspense>
  );
};

export default EmailLogs;
